import { useEffect, useState } from 'react';
import { useAppSelector } from 'store/store';
import { getFeatureFlagByNameSelector } from 'store/selectors';
import { IPersonalizationService } from 'types';
import { PERSONALIZATION_SERVICE } from 'utility/constants';
import { FeatureFlagsNames } from 'config/featureFlags';
import useLogger from './useLogger';

interface UsePersonalizationProps {
  flagName: FeatureFlagsNames;
  personalizationKey: string;
  originalData: unknown;
}

const usePersonalization = ({
  flagName,
  personalizationKey,
  originalData,
}: UsePersonalizationProps) => {
  const personalizationEnabled = useAppSelector(getFeatureFlagByNameSelector(flagName));

  const [isLoadingPersonalization, setIsLoadingPersonalization] = useState(true);
  const [personalizationData, setPersonalizationData] = useState<unknown>(null);
  const logger = useLogger();

  useEffect(() => {
    (async () => {
      if (!personalizationEnabled) {
        setIsLoadingPersonalization(false);
        return;
      }

      if (typeof window.dcsgPz?.services?.loadService !== 'function') {
        setIsLoadingPersonalization(false);
        logger.warn('window.dcsgPz.services.loadServices function is not available');
        return;
      }

      try {
        const service = (await window.dcsgPz.services.loadService(
          PERSONALIZATION_SERVICE,
        )) as IPersonalizationService;
        const data = await service.getPersonalizationData(personalizationKey, originalData, 2000);

        if (data !== null) {
          setPersonalizationData(data);
        }
      } catch (error) {
        logger.warn('Error updating personalized data:', String(error));
      } finally {
        setIsLoadingPersonalization(false);
      }
    })();
  }, [personalizationEnabled, personalizationKey]);

  return {
    personalizationData,
    isLoadingPersonalization,
  };
};

export default usePersonalization;
